import type { LayoutItem, ResponsiveLayouts } from 'react-grid-layout/legacy';
import type { WidgetDef, WidgetLayoutBreakpoint, WidgetLayoutUpdate } from '../types';

// Grid column counts (must match the DashboardGrid `cols` prop).
const COLS_LG = 12;
const COLS_MD = 6;

// The backend only accepts these widths/heights on every breakpoint, but RGL
// lets you drag-resize to any integer — so snap to the nearest allowed value.
const ALLOWED_W = [3, 4, 6, 8, 12];
const ALLOWED_H = [2, 3, 4, 6];

export function snap(value: number, allowed: number[]): number {
  return allowed.reduce(
    (best, a) => (Math.abs(a - value) < Math.abs(best - value) ? a : best),
    allowed[0],
  );
}

function sameBp(a: WidgetLayoutBreakpoint, b: WidgetLayoutBreakpoint): boolean {
  return a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}

/**
 * Builds per-widget layout updates from the desktop (lg) RGL layout, snapping to
 * the allowed grid sizes and deriving tablet/mobile from it (tablet ≤6 cols,
 * mobile full width). Only widgets whose desktop layout changed are returned, so
 * untouched widgets are not re-persisted.
 */
export function changedLayoutUpdates(
  widgets: WidgetDef[],
  layouts: ResponsiveLayouts,
): WidgetLayoutUpdate[] {
  const lg = new Map(((layouts['lg'] as LayoutItem[] | undefined) ?? []).map((l) => [l.i, l]));

  const updates: WidgetLayoutUpdate[] = [];
  for (const widget of widgets) {
    const item = lg.get(widget.id);
    const src = item ?? widget.layout.desktop;
    const w = snap(src.w, ALLOWED_W);
    const h = snap(src.h, ALLOWED_H);
    const desktop: WidgetLayoutBreakpoint = {
      x: Math.max(0, Math.min(src.x, COLS_LG - w)),
      y: Math.max(0, src.y),
      w,
      h,
    };
    if (sameBp(desktop, widget.layout.desktop)) continue;

    const tabletW = Math.min(w, COLS_MD);
    updates.push({
      id: widget.id,
      desktop,
      tablet: { x: 0, y: desktop.y, w: tabletW, h },
      mobile: { x: 0, y: desktop.y, w: 12, h },
    });
  }
  return updates;
}
